import { ColumnProps } from "antd/lib/table";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  LayoutCell,
  LayoutHeader,
  OneLineText,
} from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { useHistory } from "react-router-dom";
import { menuReportRepository } from "repositories/menu-report-repository";
import {
  detailService,
  ModelActionEnum,
} from "services/page-services/detail-service";
import { MenuReport } from "models/MenuReport";
import { queryStringService } from "services/page-services/query-string-service";
import {
  FileModel,
  FileTemplate,
  FileTemplateFilter,
} from "models/FileTemplate";
import { Switch } from "antd";
import { Organization } from "models/Organization";
import { renderMasterIndex } from "helpers/table";
import { useFileTemplateInputModal } from "./FileTemplateInputModalHook";
import { finalize } from "rxjs";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { AppUser } from "models/AppUser";

export function useMenuReportHistoryPreview() {
  const [translate] = useTranslation();

  const [isSpinning, setIsSpinning] = useState(false);

  const { model, dispatch } = detailService.useModel<MenuReport>(MenuReport);

  const { versionId }: any = queryStringService.useGetQueryString("versionId");
  const isDetail = React.useMemo(() => versionId !== null, [versionId]);

  React.useEffect(() => {
    if (isDetail) {
      setIsSpinning(true);
      const subscription = menuReportRepository
        .getRequestHistory(Number(versionId))
        .pipe(finalize(() => setIsSpinning(false)))
        .subscribe({
          next: (res) =>
            dispatch({ type: ModelActionEnum.SET, payload: res?.content }),
          error: (_err) => {},
        });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dispatch, isDetail, versionId]);

  const history = useHistory();
  const handleGoBack = React.useCallback(() => {
    history.goBack();
  }, [history]);

  const {
    modelFilter: fileTemplateFilter,
    dispatchFilter: dispatchFileTemplateFilter,
  } = filterService.useModelFilter(FileTemplateFilter);

  const { list: listFileTemplate } = listService.useLocalList(
    model?.fileTemplates,
    fileTemplateFilter
  );

  const {
    handleOpenModal,
    handleCloseModal,
    fileTemplateInputColumns,
    fileTemplateInputsInternal,
    visible,
  } = useFileTemplateInputModal();

  const renderOrganizationMappings = React.useCallback(
    (values: Organization[]) => {
      const listName = values.map(
        (value: Organization) =>
          `${value?.usingOrganization?.code} - ${value?.usingOrganization?.name}`
      );
      return listName.join(", ");
    },
    []
  );

  const columns: ColumnProps<FileTemplate>[] = React.useMemo(() => {
    return [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
          />
        ),
        key: "index",
        width: 80,
        align: "center",
        render: renderMasterIndex<FileTemplate>(),
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.organization")}
          />
        ),
        key: nameof("organization"),
        dataIndex: nameof("organization"),
        render(...params: [Organization, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.creator")}
          />
        ),
        key: nameof("creator"),
        dataIndex: nameof("creator"),
        render(...params: [AppUser, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]?.displayName} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.name")}
          />
        ),
        key: nameof("name"),
        dataIndex: nameof("name"),
        render(...params: [string, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.file")}
          />
        ),
        width: 400,
        key: nameof("file"),
        dataIndex: nameof("file"),
        render(...params: [FileModel, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <a download={params[0]?.name} href={params[0]?.url}>
                <OneLineText className="text-link" value={params[0]?.name} />
              </a>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.fileTemplateOrganizationMappings")}
          />
        ),
        key: nameof("fileTemplateOrganizationMappings"),
        dataIndex: nameof("fileTemplateOrganizationMappings"),
        render(...params: [Organization[], FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText
                value={renderOrganizationMappings(params[0])}
                countCharacters={35}
              />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.selfDefineInput")}
          />
        ),
        width: 120,
        key: nameof("selfDefineInput"),
        dataIndex: nameof("selfDefineInput"),
        render(...params: [number, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <Switch
                checked={params[1].selfDefineInputId === 1 ? true : false}
                disabled
              />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.selfDefineInputId")}
          />
        ),
        width: 120,
        key: nameof("selfDefineInputId"),
        dataIndex: nameof("selfDefineInputId"),
        render(...params: [number, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              {params[0] ? (
                <Button
                  type="link"
                  onClick={() => {
                    handleOpenModal(params[1]);
                  }}
                >
                  {"Click để xem"}
                </Button>
              ) : null}
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplates.status")}
          />
        ),
        width: 120,
        key: nameof("order"),
        dataIndex: nameof("order"),
        render(...params: [number, FileTemplate, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <Switch
                checked={params[1].statusId === 1 ? true : false}
                disabled
              />
            </LayoutCell>
          );
        },
      },
    ];
  }, [handleOpenModal, renderOrganizationMappings, translate]);

  return {
    columns,
    isSpinning,
    model,
    fileTemplateInputColumns,
    fileTemplateInputsInternal,
    visible,
    listFileTemplate,
    dispatchFileTemplateFilter,
    handleCloseModal,
    handleGoBack,
  };
}
